import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Check, Radio, RefreshCw, ShoppingCart, Headphones, Users } from 'lucide-react';
import { useAppContext, Package } from '../context/AppContext';
export function Pricing() {
  const [isYearly, setIsYearly] = useState(false);
  const {
    packages,
    addToCart
  } = useAppContext();
  const hostingPlans = packages.filter(p => p.category === 'hosting' && p.active);
  const radioPlans = packages.filter(p => p.category === 'radio' && p.active);
  const resellerPlans = packages.filter(p => p.category === 'reseller' && p.active);
  const shoutcastPlans = packages.filter(p => p.category === 'shoutcast' && p.active);
  const shoutcastResellerPlans = packages.filter(p => p.category === 'shoutcast-reseller' && p.active);
  const handleAddToCart = (plan: Package) => {
    addToCart({
      packageId: plan.id,
      name: plan.name,
      category: plan.category,
      price: isYearly ? plan.yearlyPrice : plan.monthlyPrice,
      billing: isYearly ? 'yearly' : 'monthly',
      quantity: 1
    });
  };
  const renderPlanCards = (planList: Package[]) => <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-6xl mx-auto" data-id="element-1285">
      {planList.map((plan, index) => <motion.div key={plan.id} initial={{
      opacity: 0,
      y: 20
    }} whileInView={{
      opacity: 1,
      y: 0
    }} viewport={{
      once: true
    }} transition={{
      duration: 0.5,
      delay: index * 0.1
    }} className={`relative rounded-3xl p-8 ${plan.highlighted ? 'bg-slate-800 border-2 border-fox-500 shadow-[0_0_30px_rgba(234,88,12,0.15)] transform md:-translate-y-4' : 'bg-slate-900 border border-slate-800'}`} data-id="element-1286">
          {plan.highlighted && <div className="absolute top-0 left-1/2 -translate-x-1/2 -translate-y-1/2 bg-gradient-to-r from-fox-600 to-amber-500 text-white px-4 py-1 rounded-full text-sm font-bold shadow-lg" data-id="element-1287">
              Most Popular
            </div>}

          <div className="mb-8" data-id="element-1288">
            <h4 className="text-xl font-heading font-bold text-white mb-2" data-id="element-1289">
              {plan.name}
            </h4>
            <p className="text-slate-400 text-sm h-10" data-id="element-1290">{plan.description}</p>
          </div>

          <div className="mb-8" data-id="element-1291">
            <span className="text-4xl font-heading font-bold text-white" data-id="element-1292">
              ${isYearly ? plan.yearlyPrice : plan.monthlyPrice}
            </span>
            <span className="text-slate-400" data-id="element-1293">/{isYearly ? 'yr' : 'mo'}</span>
          </div>

          <ul className="space-y-4 mb-8" data-id="element-1294">
            {plan.features.map((feature, i) => <li key={i} className="flex items-start gap-3" data-id="element-1295">
                <div className="mt-1 bg-fox-500/20 p-0.5 rounded-full shrink-0" data-id="element-1296">
                  <Check size={14} className="text-fox-500" data-id="element-1297" />
                </div>
                <span className="text-slate-300 text-sm" data-id="element-1298">{feature}</span>
              </li>)}
          </ul>

          <button onClick={() => handleAddToCart(plan)} className={`w-full py-3 rounded-xl font-medium transition-all flex items-center justify-center gap-2 ${plan.highlighted ? 'bg-fox-600 hover:bg-fox-500 text-white shadow-lg hover:shadow-fox-500/25' : 'bg-slate-800 hover:bg-slate-700 text-white border border-slate-700'}`} data-id="element-1299">
            <ShoppingCart size={16} data-id="element-1300" />
            Add to Cart
          </button>
        </motion.div>)}
    </div>;
  const renderSection = (plans: Package[], icon: React.ReactNode, title: string, description: string) => plans.length > 0 && <motion.div initial={{
    opacity: 0
  }} whileInView={{
    opacity: 1
  }} viewport={{
    once: true
  }} className="mb-8" data-id="element-1301">
        <div className="text-center max-w-3xl mx-auto mb-12" data-id="element-1302">
          <div className="flex items-center justify-center gap-3 mb-4" data-id="element-1303">
            {icon}
            <h3 className="text-2xl md:text-4xl font-heading font-bold text-white" data-id="element-1304">
              {title}
            </h3>
          </div>
          <p className="text-slate-400 text-lg" data-id="element-1305">{description}</p>
        </div>
        {renderPlanCards(plans)}
      </motion.div>;
  return <section id="pricing" className="py-24 bg-slate-900 relative" data-id="element-1306">
      <div className="absolute inset-0 bg-[url('https://www.transparenttextures.com/patterns/cubes.png')] opacity-[0.02]" data-id="element-1307" />

      <div className="container mx-auto px-4 md:px-6 relative z-10" data-id="element-1308">
        {/* Header + Toggle */}
        <div className="text-center max-w-3xl mx-auto mb-16" data-id="element-1309">
          <h2 className="text-fox-500 font-semibold tracking-wide uppercase text-sm mb-3" data-id="element-1310">
            Pricing Plans
          </h2>
          <h3 className="text-3xl md:text-5xl font-heading font-bold text-white mb-6" data-id="element-1311">
            Simple, transparent pricing
          </h3>

          <div className="flex items-center justify-center gap-4 mt-8" data-id="element-1312">
            <span className={`text-sm font-medium ${!isYearly ? 'text-white' : 'text-slate-400'}`} data-id="element-1313">
              Monthly
            </span>
            <button onClick={() => setIsYearly(!isYearly)} className="w-14 h-7 bg-slate-700 rounded-full p-1 relative transition-colors hover:bg-slate-600 focus:outline-none" data-id="element-1314">
              <motion.div className="w-5 h-5 bg-fox-500 rounded-full" animate={{
              x: isYearly ? 28 : 0
            }} transition={{
              type: 'spring',
              stiffness: 500,
              damping: 30
            }} data-id="element-1315" />
            </button>
            <span className={`text-sm font-medium flex items-center gap-2 ${isYearly ? 'text-white' : 'text-slate-400'}`} data-id="element-1316">
              Yearly{' '}
              <span className="bg-amber-500/20 text-amber-400 text-xs px-2 py-0.5 rounded-full" data-id="element-1317">
                Save 20%
              </span>
            </span>
          </div>
        </div>

        {/* Web Hosting */}
        {hostingPlans.length > 0 && <motion.div initial={{
        opacity: 0
      }} whileInView={{
        opacity: 1
      }} viewport={{
        once: true
      }} className="mb-8" data-id="element-1318">
            <div className="flex items-center gap-3 mb-8 max-w-6xl mx-auto" data-id="element-1319">
              <div className="w-10 h-10 rounded-xl bg-fox-500/10 flex items-center justify-center" data-id="element-1320">
                <RefreshCw size={20} className="text-fox-500" data-id="element-1321" />
              </div>
              <h3 className="text-2xl font-heading font-bold text-white" data-id="element-1322">
                Web Hosting
              </h3>
            </div>
            {renderPlanCards(hostingPlans)}
          </motion.div>}

        {hostingPlans.length > 0 && radioPlans.length > 0 && <div className="max-w-6xl mx-auto my-20 border-t border-slate-800" data-id="element-1323" />}

        {/* Radio Station Rentals */}
        {renderSection(radioPlans, <div className="w-10 h-10 rounded-xl bg-amber-500/10 flex items-center justify-center" data-id="element-1324">
            <Radio size={20} className="text-amber-500" data-id="element-1325" />
          </div>, 'Radio Station Rentals', 'Launch your own internet radio station with our fully managed streaming servers. Auto DJ, live broadcasting, and listener analytics included.')}

        {radioPlans.length > 0 && resellerPlans.length > 0 && <div className="max-w-6xl mx-auto my-20 border-t border-slate-800" data-id="element-1326" />}

        {/* Reseller Accounts */}
        {renderSection(resellerPlans, <div className="w-10 h-10 rounded-xl bg-fox-500/10 flex items-center justify-center" data-id="element-1327">
            <RefreshCw size={20} className="text-fox-500" data-id="element-1328" />
          </div>, 'Reseller Accounts', 'Start your own hosting business with our white-label reseller plans. Full WHM access, custom branding, and WHMCS billing integration.')}

        {resellerPlans.length > 0 && shoutcastPlans.length > 0 && <div className="max-w-6xl mx-auto my-20 border-t border-slate-800" data-id="element-1329" />}

        {/* Shoutcast Packages */}
        {renderSection(shoutcastPlans, <div className="w-10 h-10 rounded-xl bg-purple-500/10 flex items-center justify-center" data-id="element-1330">
            <Headphones size={20} className="text-purple-500" data-id="element-1331" />
          </div>, 'Shoutcast Packages', 'Dedicated Shoutcast v2 DNAS servers with Centova Cast control panel. Stream your audio content to the world with professional-grade infrastructure.')}

        {shoutcastPlans.length > 0 && shoutcastResellerPlans.length > 0 && <div className="max-w-6xl mx-auto my-20 border-t border-slate-800" data-id="element-1332" />}

        {/* Shoutcast Reseller */}
        {renderSection(shoutcastResellerPlans, <div className="w-10 h-10 rounded-xl bg-cyan-500/10 flex items-center justify-center" data-id="element-1333">
            <Users size={20} className="text-cyan-500" data-id="element-1334" />
          </div>, 'Shoutcast Reseller', 'Resell Shoutcast hosting under your own brand. Full Centova Cast reseller panel, white-label branding, and client management tools included.')}
      </div>
    </section>;
}